import { MouseEvent, ReactElement, ReactNode } from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import thumbNailImage from '../../assets/icons/thumbNailImage.svg';
import { FileIcon } from '../../bombiix-icon';

export type BombiixDropzoneType = 'image' | 'file';

export interface BombiixDropzoneProps {
  onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  type?: BombiixDropzoneType;
  accept?: string;
  preview?: ReactNode;
  footer?: ReactNode;
  showExtensions?: boolean;
  className?: string;
}

/**
 * Bombiix master-design upload drop box (Figma node 5030:403681 — Upload Image /
 * Upload PDF). A bordered frame around a grey, shadowed box with a 120px glyph
 * and a "Click to upload or drag and drop" line. `preview` replaces the glyph and
 * turns off click-to-open so the content can be interacted with; `footer` sits
 * under the text. `file` swaps in the document glyph and can list the accepted
 * extensions.
 * @param props - Drop handler, type, accepted extensions and optional slots.
 * @returns The drop box.
 */
export function BombiixDropzone({ onDrop, type = 'image', accept, preview, footer, showExtensions = false, className }: BombiixDropzoneProps): ReactElement {
  const { t } = useTranslation('assetLibraryAssets');
  const hasPreview = Boolean(preview);
  const isFile = type === 'file';
  const frameClass = ['bombiix-image-dropzone-frame', isFile ? 'bombiix-image-dropzone-frame--file' : '', className].filter(Boolean).join(' ');

  return (
    <div className={frameClass}>
      <Dropzone {...(accept ? { accept } : {})} onDrop={onDrop} noClick={hasPreview} noKeyboard={hasPreview}>
        {({ getRootProps, getInputProps, isDragActive, open }) => (
          <section
            {...getRootProps({
              className: [
                'bombiix-image-dropzone',
                isFile ? 'bombiix-image-dropzone--file' : '',
                isDragActive ? 'bombiix-image-dropzone--active' : '',
                hasPreview ? 'bombiix-image-dropzone--filled' : '',
              ]
                .filter(Boolean)
                .join(' '),
            })}
          >
            <input {...getInputProps()} />
            {preview ? <div className="bombiix-image-dropzone__preview">{preview}</div> : renderGlyph(isFile)}
            <div className="bombiix-image-dropzone__text">
              {isDragActive ? (
                <p className="bombiix-image-dropzone__hint">{t('assets.dropHere')}</p>
              ) : (
                <p className="bombiix-image-dropzone__hint">
                  <button type="button" className="bombiix-image-dropzone__link" onClick={(event: MouseEvent<HTMLButtonElement>) => openPicker(event, open)}>
                    {t('assets.dropzoneClick')}
                  </button>
                  {t('assets.dropzoneOr')}
                </p>
              )}
              {showExtensions && accept ? (
                <p className="bombiix-image-dropzone__extensions">
                  <span className="bombiix-image-dropzone__extensions-label">{t('assets.allowedFileExtension')}</span> {formatExtensions(accept)}
                </p>
              ) : null}
              {footer}
            </div>
          </section>
        )}
      </Dropzone>
    </div>
  );
}

export default BombiixDropzone;

/**
 * Renders the idle glyph for the drop box type.
 * @param isFile - True for the document glyph, false for the image glyph.
 * @returns The glyph element.
 */
function renderGlyph(isFile: boolean): ReactElement {
  if (isFile) {
    return <FileIcon size={DROPZONE_ICON_SIZE} className="bombiix-image-dropzone__icon" />;
  }
  return <img src={thumbNailImage} className="bombiix-image-dropzone__icon" alt="" aria-hidden="true" />;
}

/**
 * Opens the file picker from the link without the click also reaching the drop
 * area, which would open it a second time.
 * @param event - Click on the "Click to upload" link.
 * @param open - react-dropzone's picker opener.
 */
function openPicker(event: MouseEvent<HTMLButtonElement>, open: () => void): void {
  event.stopPropagation();
  open();
}

/**
 * Turns an `accept` string into the list shown to the user: dotted extensions
 * only, de-duplicated, comma separated.
 * @param accept - Comma-separated accept string.
 * @returns The display list.
 */
function formatExtensions(accept: string): string {
  const extensions = accept
    .split(',')
    .map((extension) => extension.trim())
    .filter((extension) => extension.startsWith('.'));
  return [...new Set(extensions)].join(', ');
}

const DROPZONE_ICON_SIZE = 120;
